import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'

type DraftRelease = { id: number; version: string; titulo: string }
type DraftChange = { id: number; dataset: string; approval_status: 'rascunho' | 'aguardando_aprovacao' | 'aprovado' | 'rejeitado' }

const DATASET_LABELS: Record<string, string> = {
  empreendimentos: 'Empreendimentos',
  suite_help: 'Suite Help',
  qualificacoes: 'Qualificações',
  acionamentos: 'Acionamentos',
  servicos: 'Tabela de Serviços',
  irregularidades: 'Irregularidades',
  config_tarifas: 'Tabela Tarifária',
  config_negociacao: 'Regras de Negociação',
}

function getErrorMessage(err: unknown, fallback: string) {
  if (err instanceof Error && err.message) return err.message
  if (typeof err === 'object' && err !== null && 'message' in err) {
    const message = (err as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
  }
  return fallback
}

export function GlobalReleasePanel() {
  const [release, setRelease] = useState<DraftRelease | null>(null)
  const [changes, setChanges] = useState<DraftChange[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  async function load() {
    setLoading(true); setError('')
    try {
      const releaseResult = await supabase.from('releases').select('id,version,titulo').eq('status', 'rascunho').order('id', { ascending: false }).limit(1).maybeSingle()
      if (releaseResult.error) throw releaseResult.error
      const currentRelease = releaseResult.data as DraftRelease | null
      setRelease(currentRelease)
      if (!currentRelease) { setChanges([]); return }
      const changesResult = await supabase.from('draft_changes').select('id,dataset,approval_status').eq('release_id', currentRelease.id).order('id')
      if (changesResult.error) throw changesResult.error
      setChanges((changesResult.data ?? []) as DraftChange[])
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível carregar a release em preparação.'))
    } finally { setLoading(false) }
  }

  useEffect(() => { void load(); const interval = window.setInterval(() => void load(), 3000); return () => window.clearInterval(interval) }, [])

  const summary = useMemo(() => {
    const datasets = Array.from(new Set(changes.map((item) => item.dataset)))
    return {
      datasets,
      approved: changes.filter((item) => item.approval_status === 'aprovado').length,
      awaiting: changes.filter((item) => item.approval_status === 'aguardando_aprovacao').length,
      rejected: changes.filter((item) => item.approval_status === 'rejeitado').length,
      draft: changes.filter((item) => item.approval_status === 'rascunho').length,
      allApproved: changes.length > 0 && changes.every((item) => item.approval_status === 'aprovado'),
    }
  }, [changes])

  async function publish() {
    if (!release || !summary.allApproved || changes.length === 0) return
    const datasetText = summary.datasets.map((key) => DATASET_LABELS[key] ?? key).join(', ')
    if (!window.confirm(`Publicar a release ${release.version} com ${changes.length} alteração(ões) em ${summary.datasets.length} conjunto(s)?\n\nConteúdos: ${datasetText}\n\nEsta publicação é global e aplicará todas as alterações aprovadas da release.`)) return
    setSaving(true); setError(''); setMessage('')
    try {
      const result = await supabase.rpc('publish_release', { target_release_id: release.id })
      if (result.error) throw result.error
      setMessage(`Release ${release.version} publicada com sucesso.`)
      await load(); window.setTimeout(() => window.location.reload(), 700)
    } catch (err) {
      setError(getErrorMessage(err, 'Não foi possível publicar a release.'))
    } finally { setSaving(false) }
  }

  if (loading && !release) return null
  if (!release && !error) return null

  return <section className="global-release-panel">
    <div className="global-release-panel__head"><div><p className="eyebrow">PUBLICAÇÃO GLOBAL</p><h2>Release em preparação {release ? `· ${release.version}` : ''}</h2><p className="muted">Uma release pode reunir bases oficiais e configurações operacionais. A publicação aplica tudo em uma única transação.</p></div>{release && <span className="status-chip warning">Rascunho {release.version}</span>}</div>
    {message && <div className="notice success">{message}</div>}{error && <div className="notice error">{error}</div>}
    {release && <><div className="global-release-stats"><div><span>Alterações</span><strong>{changes.length}</strong></div><div><span>Conjuntos afetados</span><strong>{summary.datasets.length}</strong></div><div><span>Aprovadas</span><strong>{summary.approved}</strong></div><div><span>Aguardando</span><strong>{summary.awaiting + summary.draft + summary.rejected}</strong></div></div><div className="global-release-datasets">{summary.datasets.map((dataset) => <span key={dataset}>{DATASET_LABELS[dataset] ?? dataset}</span>)}</div><div className="global-release-actions">{!summary.allApproved && <span className="muted">A publicação será liberada quando todas as alterações estiverem aprovadas.</span>}<button type="button" className="primary-button compact" disabled={saving || !summary.allApproved || changes.length === 0} onClick={() => void publish()}>{saving ? 'Publicando...' : `Publicar release ${release.version}`}</button></div></>}
  </section>
}
